import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Maintenance } from '@/types/maintenance'
import type { Equipment } from '@/types/equipment'
import { maintenanceApi } from '@/api/maintenanceApi'
import { maintenanceStatsApi } from '@/api/maintenanceStatsApi'
import type { MaintenanceStats } from '@/types/maintenance'

export const useMaintenanceStore = defineStore('maintenance', () => {
  const maintenance = ref<Maintenance[]>([])
  const maintenanceCount = ref(0) // Store total count for pagination
  const currentMaintenance = ref<Maintenance | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const stats = ref<MaintenanceStats | null>(null)

  // Updated to handle paginated response and enrich records with equipment
  const fetchMaintenance = async (params?: Record<string, unknown>) => {
    loading.value = true
    error.value = null
    try {
      const response = await maintenanceApi.getAll(params)
      const raw = response.results || []
      // Enrich each record with equipment if available (from API or equipment store)
      const equipmentStore = (await import('@/stores/equipmentStore')).useEquipmentStore()
      if (!Array.isArray(equipmentStore.equipment) || equipmentStore.equipment.length === 0) {
        try {
          await equipmentStore.fetchEquipment()
        } catch {
          // ignore
        }
      }
      const enriched = raw.map((rec: unknown) => {
        const recObj = rec as Record<string, unknown>

        let eqId: number | undefined
        const rawEq = recObj['equipment']
        if (typeof rawEq === 'number') {
          eqId = rawEq
        } else if (rawEq && typeof rawEq === 'object' && 'id' in rawEq) {
          eqId = Number((rawEq as Record<string, unknown>).id)
        }

        if (!eqId && 'equipment_id' in recObj) {
          eqId = Number(recObj['equipment_id'])
        }

        const foundEq = eqId && Array.isArray(equipmentStore.equipment)
          ? equipmentStore.equipment.find((e) => e.id === eqId)
          : undefined
        const finalEq = foundEq || (typeof rawEq === 'object' ? rawEq : undefined)

        return {
          ...recObj,
          equipment_id: eqId,
          equipment: finalEq,
        } as unknown as Maintenance
      })

      maintenance.value = enriched as Maintenance[]
      maintenanceCount.value =
        typeof response.count === 'number' ? response.count : maintenance.value.length
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch maintenance'
    } finally {
      loading.value = false
    }
  }

  const fetchMaintenanceById = async (id: number) => {
    loading.value = true
    error.value = null
    try {
      const fetched = await maintenanceApi.getById(id)

      // Enrich with equipment
      const equipmentStore = (await import('@/stores/equipmentStore')).useEquipmentStore()
      if (!Array.isArray(equipmentStore.equipment) || equipmentStore.equipment.length === 0) {
        try {
          await equipmentStore.fetchEquipment()
        } catch {
          // ignore
        }
      }

      const recObj = fetched as unknown as Record<string, unknown>
      let eqId: number | undefined
      const rawEq = recObj['equipment']
      if (typeof rawEq === 'number') {
        eqId = rawEq
      } else if (rawEq && typeof rawEq === 'object' && 'id' in rawEq) {
        eqId = Number((rawEq as Record<string, unknown>).id)
      }
      if (!eqId && 'equipment_id' in recObj) {
        eqId = Number(recObj['equipment_id'])
      }

      const foundEq = eqId && Array.isArray(equipmentStore.equipment)
        ? equipmentStore.equipment.find((e) => e.id === eqId)
        : undefined
      const finalEq = foundEq || (typeof rawEq === 'object' ? rawEq : undefined)

      currentMaintenance.value = {
        ...(fetched as Maintenance),
        equipment_id: eqId ?? 0,
        equipment: finalEq
      } as Maintenance

    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch maintenance'
    } finally {
      loading.value = false
    }
  }

  const createMaintenance = async (data: Partial<Maintenance>) => {
    loading.value = true
    error.value = null
    try {
      const newMaintenance = await maintenanceApi.create(data)
      // enrich with equipment
      const equipmentStore = (await import('@/stores/equipmentStore')).useEquipmentStore()
      const newRec = newMaintenance as unknown as Record<string, unknown>

      let eqId: number | undefined
      const rawEq = newRec['equipment']
      if (typeof rawEq === 'number') {
        eqId = rawEq
      } else if (rawEq && typeof rawEq === 'object' && 'id' in rawEq) {
        eqId = Number((rawEq as Record<string, unknown>).id)
      }
      if (!eqId && 'equipment_id' in newRec) {
        eqId = Number(newRec['equipment_id'])
      }
      // fallback to input if not in response
      if (!eqId && 'equipment_id' in data) {
        eqId = Number((data as any).equipment_id)
      }

      const foundEq = eqId && Array.isArray(equipmentStore.equipment)
        ? equipmentStore.equipment.find((e) => e.id === eqId)
        : undefined
      const finalEq = foundEq || (typeof rawEq === 'object' ? rawEq : undefined)

      const enriched = {
        ...(newMaintenance as Maintenance),
        equipment_id: eqId ?? 0,
        equipment: finalEq,
      } as Maintenance
      maintenance.value.push(enriched)
      return enriched
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create maintenance'
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateMaintenance = async (id: number, data: Partial<Maintenance>) => {
    loading.value = true
    error.value = null
    try {
      const updatedMaintenance = await maintenanceApi.update(id, data)
      // enrich updated record
      const equipmentStore = (await import('@/stores/equipmentStore')).useEquipmentStore()
      const updRec = updatedMaintenance as unknown as Record<string, unknown>

      let eqId: number | undefined
      const rawEq = updRec['equipment']
      if (typeof rawEq === 'number') {
        eqId = rawEq
      } else if (rawEq && typeof rawEq === 'object' && 'id' in rawEq) {
        eqId = Number((rawEq as Record<string, unknown>).id)
      }
      if (!eqId && 'equipment_id' in updRec) {
        eqId = Number(updRec['equipment_id'])
      }

      const foundEq = eqId && Array.isArray(equipmentStore.equipment)
        ? equipmentStore.equipment.find((e) => e.id === eqId)
        : undefined
      const finalEq = foundEq || (typeof rawEq === 'object' ? rawEq : undefined)

      const enriched = {
        ...(updatedMaintenance as Maintenance),
        equipment_id: eqId ?? 0,
        equipment: finalEq,
      } as Maintenance
      const index = maintenance.value.findIndex((m) => m.id === id)
      if (index !== -1) {
        maintenance.value[index] = enriched
      }
      return enriched
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update maintenance'
      throw err
    } finally {
      loading.value = false
    }
  }

  const deleteMaintenance = async (id: number) => {
    loading.value = true
    error.value = null
    try {
      await maintenanceApi.delete(id)
      maintenance.value = maintenance.value.filter((m) => m.id !== id)
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete maintenance'
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchStats = async () => {
    loading.value = true
    error.value = null
    try {
      stats.value = await maintenanceStatsApi.getStats()
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch stats'
    } finally {
      loading.value = false
    }
  }

  // Computed properties for stats and filtering
  const scheduledMaintenance = computed(() =>
    maintenance.value.filter((m) => m.maintenance_type === 'scheduled'),
  )
  const repairMaintenance = computed(() =>
    maintenance.value.filter((m) => m.maintenance_type === 'repair'),
  )
  const inspectionMaintenance = computed(() =>
    maintenance.value.filter((m) => m.maintenance_type === 'inspection'),
  )
  const totalMaintenanceCost = computed(() => {
    return maintenance.value.reduce((sum, m) => {
      const cost = typeof m.cost === 'number' ? m.cost : m.cost ? Number(m.cost) : 0
      return sum + (Number.isFinite(cost) ? cost : 0)
    }, 0)
  })

  // Upcoming maintenance within N days
  const upcoming = computed(() => {
    const now = new Date()
    const inNDays = (n: number) => {
      const d = new Date()
      d.setDate(d.getDate() + n)
      return d
    }
    return maintenance.value.filter((m) => {
      if (!m.maintenance_date) return false
      const date = new Date(m.maintenance_date)
      return date >= now && date <= inNDays(upcomingDays.value)
    })
  })
  const upcomingDays = ref(30) // default to 30 days

  // Fetch only upcoming maintenance (optionally with custom days)
  const fetchUpcoming = async (days = 30) => {
    upcomingDays.value = days
    await fetchMaintenance() // just refetch all, filter in computed
  }

  return {
    maintenance,
    maintenanceCount,
    currentMaintenance,
    loading,
    error,
    fetchMaintenance,
    fetchMaintenanceById,
    createMaintenance,
    updateMaintenance,
    deleteMaintenance,
    scheduledMaintenance,
    repairMaintenance,
    inspectionMaintenance,
    totalMaintenanceCost,
    upcoming,
    fetchUpcoming,
    stats,
    fetchStats,
  }
})
